import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { BBWTMessageService } from '../../services';
import { sha512 } from '../../utils/hash';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ControlsOf } from 'src/app/utils/controls-of';

export enum ResetPasswordStatus {
  Loading = 0,
  ResetPassword = 1,
  OperationCompleted = 2,
  ResetPasswordError = 4,
}

export interface PasswordForm {
  password: string;
  confirmPassword: string;
}

@Component({
  selector: 'reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [],
})
export class ResetPasswordComponent implements OnInit {
  private readonly PASSWORD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&])[A-Za-z0-9@$!%*?&_-]{8,}$/;

  private passwordMatchValidator: ValidatorFn = (group: AbstractControl) => {
    const password = group.get('password')?.value ?? '';
    const confirm = group.get('confirmPassword')?.value ?? '';
    if (password && confirm && password !== confirm) return { passwordsDontMatch: true };
    return null;
  };

  public Status: ResetPasswordStatus;

  /** True when reached from an invite email, so the page reads as a first time password setup. */
  public isNewAccount = false;

  private Code: string;
  public UserId: string;

  public passwordForm: FormGroup<ControlsOf<PasswordForm>> = new FormGroup<
    ControlsOf<PasswordForm>
  >(
    {
      password: new FormControl(null, [
        Validators.required,
        Validators.pattern(this.PASSWORD_REGEX),
      ]),
      confirmPassword: new FormControl(null, Validators.required),
    },
    {
      validators: [this.passwordMatchValidator],
    },
  );

  get passwordControl() {
    return this.passwordForm.controls.password;
  }
  get confirmPasswordConrol() {
    return this.passwordForm.controls.confirmPassword;
  }

  constructor(
    public router: Router,
    activatedRoute: ActivatedRoute,
    private http: HttpClient,
    public messageService: BBWTMessageService,
  ) {
    activatedRoute.queryParams.subscribe((params) => {
      this.UserId = params['userId'];
      this.Code = params['code'];
    });

    activatedRoute.data.subscribe((data) => {
      this.isNewAccount = !!data['isNewAccount'];
    });
  }

  public get title(): string {
    return this.isNewAccount ? 'Set your password' : 'Reset password';
  }

  public get saveLabel(): string {
    return this.isNewAccount ? 'Set Password' : 'Reset Password';
  }

  public get completedMessage(): string {
    return this.isNewAccount ? 'Your password has been set.' : 'Your password has been reset.';
  }

  public get requestNewLinkLabel(): string {
    return this.isNewAccount
      ? 'Click here to request a new link'
      : 'Click here to request a new reset password link';
  }

  public get saveDisabled(): boolean {
    return this.passwordForm.invalid || this.Status == ResetPasswordStatus.Loading;
  }

  ngOnInit() {}

  sendRequestPasswordLink() {
    this.http
      .post<any>('/api/account/recoverpassword', {
        userId: this.UserId,
      })
      .toPromise()
      .then(() => {
        this.messageService.ShowSuccess('Request new link', 'A new link has been send');
      });
  }

  Save() {
    const password = this.passwordForm.controls.password.value;
    var crpPassword = sha512(password);
    this.Status = ResetPasswordStatus.Loading;

    this.http
      .post<any>('/api/account/resetpassword', {
        Code: this.Code,
        Password: crpPassword,
        UserId: this.UserId,
      })
      .toPromise()
      .then((recoverResult) => {
        if (recoverResult.isSuccess == true) {
          // show link to login
          this.messageService.ShowSuccess(
            this.title,
            this.isNewAccount ? 'Password set successfully' : 'Password changed successfully',
          );
          this.Status = ResetPasswordStatus.OperationCompleted;
        } else {
          var error = recoverResult.error;
          this.messageService.ShowError(this.title, error, undefined, 'contactEngineiusHtmlBody');
          this.Status = ResetPasswordStatus.ResetPasswordError;
        }
      })
      .catch(({ error, statusText }) => {
        this.messageService.ShowError(this.title, error.error ? error.error : statusText);
        this.Status = ResetPasswordStatus.ResetPasswordError;
      });
  }
}
